import { Box, CircularProgress, Typography } from '@mui/material';
import { QueryClient, useQuery } from '@tanstack/react-query';
import React, { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LoaderFunctionArgs, Navigate, useParams } from 'react-router-dom';
import { logbookConfigurationQueryOptions } from './api/queryOptions';
import { OCRMainContainer } from './components/containers/OCRMainContainer';
import { useOCRStore } from './store/ocrStore';

export const logbookConfigurationLoader =
    (queryClient: QueryClient) =>
    async ({ params }: LoaderFunctionArgs) => {
        const { logbookAssetConfigurationId } = params;
        if (logbookAssetConfigurationId) {
            await queryClient.prefetchQuery(logbookConfigurationQueryOptions(logbookAssetConfigurationId));
        }
        return null;
    };

interface LogbookConfigurationEditorProps {
    readonly logbookAssetConfigurationId: string | undefined;
}

const LogbookConfigurationEditor: React.FC<LogbookConfigurationEditorProps> = ({ logbookAssetConfigurationId }) => {
    const { t } = useTranslation();
    const { plantId } = useParams<{ plantId: string }>();
    const { data, isError } = useQuery(logbookConfigurationQueryOptions(logbookAssetConfigurationId));
    const selectedTemplate = useOCRStore((state) => state.selectedTemplate);
    const populateStateForEdit = useOCRStore((state) => state.populateStateForEdit);
    const resetAll = useOCRStore((state) => state.resetAll);

    const isEditMode = logbookAssetConfigurationId !== undefined;

    useLayoutEffect(() => {
        if (data) populateStateForEdit(data);
    }, [data, populateStateForEdit]);

    useLayoutEffect(() => resetAll, [resetAll]);

    if (isEditMode && isError) {
        return <Navigate to={`/logbooks/${plantId}`} replace />;
    }

    if (isEditMode && !selectedTemplate) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{t('logbookConfig.loadingConfiguration', 'Loading Configuration...')}</Typography>
            </Box>
        );
    }

    return <OCRMainContainer isEditMode={isEditMode} />;
};

export const LogbookConfigurationPage: React.FC = () => {
    const { logbookAssetConfigurationId } = useParams<{ logbookAssetConfigurationId: string }>();
    return (
        <LogbookConfigurationEditor
            key={logbookAssetConfigurationId ?? 'new'}
            logbookAssetConfigurationId={logbookAssetConfigurationId}
        />
    );
};
